import { useState } from 'react';
import { ReactionEmoji } from '../../hooks/useReactions';
import { ReactionPicker } from './ReactionPicker';

interface VideoControlsProps {
  isMuted: boolean;
  isVideoEnabled: boolean;
  isScreenSharing: boolean;
  isHost: boolean;
  onToggleMute: () => void;
  onToggleVideo: () => void;
  onToggleScreenShare: () => void;
  onLeave: () => void;
  onEnd: () => void;
  // New props for enhanced features
  isChatOpen?: boolean;
  unreadChatCount?: number;
  onToggleChat?: () => void;
  isHandRaised?: boolean;
  onToggleHand?: () => void;
  onSendReaction?: (emoji: ReactionEmoji) => void;
  onOpenBackgroundSettings?: () => void;
  onMuteAll?: () => void;
  onAddParticipant?: () => void;
}

export function VideoControls({
  isMuted,
  isVideoEnabled,
  isScreenSharing,
  isHost,
  onToggleMute,
  onToggleVideo,
  onToggleScreenShare,
  onLeave,
  onEnd,
  // New props
  isChatOpen = false,
  unreadChatCount = 0,
  onToggleChat,
  isHandRaised = false,
  onToggleHand,
  onSendReaction,
  onOpenBackgroundSettings,
  onMuteAll,
  onAddParticipant,
}: VideoControlsProps) {
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  return (
    <div className="bg-gray-800 px-2 sm:px-4 py-3 sm:py-4 safe-area-bottom">
      {/* Main controls row */}
      <div className="flex items-center justify-center gap-2 sm:gap-3">
        {/* Mute/Unmute - always visible */}
        <button
          onClick={onToggleMute}
          className={`w-12 h-12 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors active:scale-95 ${
            isMuted ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
          }`}
          title={isMuted ? 'Unmute' : 'Mute'}
        >
          {isMuted ? (
            <svg className="w-5 h-5 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            </svg>
          ) : (
            <svg className="w-5 h-5 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          )}
        </button>

        {/* Camera On/Off - always visible */}
        <button
          onClick={onToggleVideo}
          className={`w-12 h-12 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors active:scale-95 ${
            !isVideoEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-700 hover:bg-gray-600'
          }`}
          title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
        >
          {isVideoEnabled ? (
            <svg className="w-5 h-5 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          ) : (
            <svg className="w-5 h-5 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
            </svg>
          )}
        </button>

        {/* Chat Toggle - always visible on mobile (important feature) */}
        {onToggleChat && (
          <button
            onClick={onToggleChat}
            className={`relative w-12 h-12 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors active:scale-95 ${
              isChatOpen ? 'bg-aalaw-red hover:bg-aalaw-red/90' : 'bg-gray-700 hover:bg-gray-600'
            }`}
            title="Chat"
          >
            <svg className="w-5 h-5 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {unreadChatCount > 0 && !isChatOpen && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                {unreadChatCount > 9 ? '9+' : unreadChatCount}
              </span>
            )}
          </button>
        )}

        {/* Screen Share - hidden on mobile (not supported well) */}
        <button
          onClick={onToggleScreenShare}
          className={`hidden sm:flex w-12 h-12 rounded-full items-center justify-center transition-colors active:scale-95 ${
            isScreenSharing ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-700 hover:bg-gray-600'
          }`}
          title={isScreenSharing ? 'Stop sharing' : 'Share screen'}
        >
          <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </button>

        {/* Raise Hand - visible on mobile */}
        {onToggleHand && (
          <button
            onClick={onToggleHand}
            className={`w-12 h-12 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-colors active:scale-95 ${
              isHandRaised ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-gray-700 hover:bg-gray-600'
            }`}
            title={isHandRaised ? 'Lower hand' : 'Raise hand'}
          >
            <svg className="w-5 h-5 sm:w-5 sm:h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
            </svg>
          </button>
        )}

        {/* Reactions - hidden on mobile, in more menu */}
        {onSendReaction && (
          <div className="relative hidden sm:block">
            <ReactionPicker onSelectReaction={onSendReaction} />
          </div>
        )}

        {/* Background Settings - hidden on mobile */}
        {onOpenBackgroundSettings && (
          <button
            onClick={onOpenBackgroundSettings}
            className="hidden sm:flex w-12 h-12 rounded-full items-center justify-center bg-gray-700 hover:bg-gray-600 transition-colors active:scale-95"
            title="Background settings"
          >
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </button>
        )}

        {/* Host Controls - hidden on mobile, in more menu */}
        {isHost && onMuteAll && (
          <button
            onClick={onMuteAll}
            className="hidden sm:flex w-12 h-12 rounded-full items-center justify-center bg-gray-700 hover:bg-gray-600 transition-colors active:scale-95"
            title="Mute all participants"
          >
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            </svg>
          </button>
        )}

        {isHost && onAddParticipant && (
          <button
            onClick={onAddParticipant}
            className="hidden sm:flex w-12 h-12 rounded-full items-center justify-center bg-gray-700 hover:bg-gray-600 transition-colors active:scale-95"
            title="Add participant"
          >
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </button>
        )}

        {/* More menu button - visible on mobile only */}
        <div className="relative sm:hidden">
          <button
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-700 hover:bg-gray-600 transition-colors active:scale-95"
            title="More options"
          >
            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>

          {/* More menu dropdown */}
          {showMoreMenu && (
            <>
              {/* Backdrop */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowMoreMenu(false)}
              />
              
              {/* Menu */}
              <div className="absolute bottom-full right-0 mb-2 w-48 bg-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
                {onSendReaction && (
                  <button
                    onClick={() => {
                      // For mobile, just send a thumbs up
                      onSendReaction('👍');
                      setShowMoreMenu(false);
                    }}
                    className="w-full px-4 py-3 text-left text-white hover:bg-gray-600 flex items-center gap-3"
                  >
                    <span className="text-lg">👍</span>
                    <span>Send Reaction</span>
                  </button>
                )}
                
                {onOpenBackgroundSettings && (
                  <button
                    onClick={() => {
                      onOpenBackgroundSettings();
                      setShowMoreMenu(false);
                    }}
                    className="w-full px-4 py-3 text-left text-white hover:bg-gray-600 flex items-center gap-3"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span>Background</span>
                  </button>
                )}

                {isHost && onMuteAll && (
                  <button
                    onClick={() => {
                      onMuteAll();
                      setShowMoreMenu(false);
                    }}
                    className="w-full px-4 py-3 text-left text-white hover:bg-gray-600 flex items-center gap-3"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                    <span>Mute All</span>
                  </button>
                )}

                {isHost && onAddParticipant && (
                  <button
                    onClick={() => {
                      onAddParticipant();
                      setShowMoreMenu(false);
                    }}
                    className="w-full px-4 py-3 text-left text-white hover:bg-gray-600 flex items-center gap-3"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                    <span>Add Participant</span>
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* Leave/End buttons */}
        {/* On mobile: show a single red end call button that either leaves or ends based on role */}
        {/* On desktop: show text buttons */}
        
        {/* Mobile: Single end call style button */}
        <button
          onClick={isHost ? onEnd : onLeave}
          className="sm:hidden w-14 h-14 bg-red-600 hover:bg-red-700 rounded-full text-white transition-colors active:scale-95 flex items-center justify-center"
          title={isHost ? "End call for everyone" : "Leave call"}
        >
          <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 01-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28a11.27 11.27 0 00-2.67-1.85.996.996 0 01-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
          </svg>
        </button>

        {/* Desktop: Text buttons */}
        <button
          onClick={onLeave}
          className="hidden sm:flex px-5 py-2.5 bg-gray-700 hover:bg-gray-600 rounded-full text-white text-sm font-medium transition-colors active:scale-95 items-center justify-center"
        >
          Leave
        </button>

        {isHost && (
          <button
            onClick={onEnd}
            className="hidden sm:flex px-5 py-2.5 bg-red-600 hover:bg-red-700 rounded-full text-white text-sm font-medium transition-colors active:scale-95 items-center justify-center"
          >
            End
          </button>
        )}
      </div>
    </div>
  );
}
